/**
 * Game State Export/Import System for Debugging Multiplayer Synchronization
 *
 * This module provides complete game state serialization for reproducing bugs.
 * Export includes: terrain bitmap, RNG state, all entities, game phase, inventories.
 */

import type { GameStateSnapshot, TerrainChange } from '../network/gameStateSync';
import type { GameSettings } from '../core/gameSettings';
import type { LogEntry } from '../core/gameLogger';

/**
 * Complete serialized game state for debugging
 */
export interface SerializedGameState {
  /** Version identifier for compatibility checking */
  version: string;
  /** Unix timestamp when state was exported */
  timestamp: number;
  /** Current game tick number */
  tick: number;
  /** Terrain generation seed */
  terrainSeed: number;
  /** RNG state for deterministic replay */
  rngState: RNGStateSnapshot;
  /** Core game state from network sync */
  gameState: GameStateSnapshot;
  /** Base64-encoded terrain ImageData */
  terrainData: string;
  /** Terrain modifications for reconstruction */
  terrainModifications?: TerrainChange[];
  /** Terrain canvas dimensions */
  terrainDimensions: { width: number; height: number };
  /** Game settings used for this session */
  settings: GameSettings;
  /** Additional entity states not in GameStateSnapshot */
  extendedEntities: ExtendedEntityState;
  /** Current water level (for sudden death) */
  waterLevel: number;
  /** Total turn count */
  totalTurnCount: number;
  /** Whether sudden death is active */
  suddenDeathActive: boolean;
  /** Log data for debugging */
  logs?: {
    entries: LogEntry[];
    sessionStart: number;
    enabledCategories: Record<string, boolean>;
  };
}

/**
 * RNG state snapshot for deterministic replay
 */
export interface RNGStateSnapshot {
  /** Current seed value */
  seed: number;
}

/**
 * Extended entity states not covered by GameStateSnapshot
 */
export interface ExtendedEntityState {
  /** Sheep entities */
  sheep: SheepSync[];
  /** Super sheep entities */
  superSheep: SuperSheepSync[];
  /** Kamikaze entities */
  kamikazes: KamikazeSync[];
  /** Fire entities */
  fires: FireSync[];
  /** Grave entities */
  graves: GraveSync[];
}

export interface SheepSync {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  facing: 'left' | 'right';
  isGrounded: boolean;
  active: boolean;
  ownerTeamId: string;
  explosionRadius: number;
  maxDamage: number;
  /** 'sheep' (hopping) or 'frog' (terrain-walking). Defaults to 'sheep'. */
  kind?: 'sheep' | 'frog';
}

export interface SuperSheepSync {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  facing: 'left' | 'right';
  isGrounded: boolean;
  active: boolean;
  ownerTeamId: string;
  isFlying: boolean;
  flyingAngle: number;
  explosionRadius: number;
  maxDamage: number;
}

export interface KamikazeSync {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  directionX: number;
  directionY: number;
  active: boolean;
  explosionRadius: number;
  maxDamage: number;
}

export interface FireSync {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  active: boolean;
  remainingDuration: number;
}

export interface GraveSync {
  id: string;
  position: { x: number; y: number };
  velocity: { x: number; y: number };
  active: boolean;
  health: number;
}

/**
 * Encodes ImageData to base64 string
 */
export function encodeTerrainData(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get 2D context from terrain canvas');
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const uint8Array = new Uint8Array(imageData.data.buffer);

  // Convert to base64 in chunks to avoid call stack issues
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }

  return btoa(binary);
}

/**
 * Decodes base64 string back to ImageData
 */
export function decodeTerrainData(base64: string, width: number, height: number): ImageData {
  const binary = atob(base64);
  const uint8Array = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    uint8Array[i] = binary.charCodeAt(i);
  }

  const imageData = new ImageData(width, height);
  imageData.data.set(new Uint8ClampedArray(uint8Array.buffer));

  return imageData;
}

/**
 * Triggers a file download in the browser
 */
export function triggerDownload(jsonString: string, filename: string): void {
  const blob = new Blob([jsonString], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename for the exported state
 */
export function generateExportFilename(): string {
  const now = new Date();
  const dateStr = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
  return `vvorms-state-${dateStr}.json`;
}

/**
 * Validates a serialized game state structure
 */
export function validateSerializedState(data: unknown): data is SerializedGameState {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const state = data as Record<string, unknown>;

  // Check required top-level fields
  const requiredFields = [
    'version',
    'timestamp',
    'tick',
    'terrainSeed',
    'rngState',
    'gameState',
    'terrainData',
    'terrainDimensions',
    'settings',
  ];

  for (const field of requiredFields) {
    if (!(field in state)) {
      console.error(`Missing required field: ${field}`);
      return false;
    }
  }

  // Validate version
  if (typeof state.version !== 'string') {
    console.error('Invalid version field');
    return false;
  }

  // Validate terrainDimensions
  const dims = state.terrainDimensions as Record<string, unknown>;
  if (typeof dims?.width !== 'number' || typeof dims?.height !== 'number') {
    console.error('Invalid terrainDimensions');
    return false;
  }

  // Validate gameState has required fields
  const gameState = state.gameState as Record<string, unknown>;
  if (
    !gameState ||
    typeof gameState.tick !== 'number' ||
    !gameState.turn ||
    !Array.isArray(gameState.worms)
  ) {
    console.error('Invalid gameState structure');
    return false;
  }

  return true;
}

/**
 * Parses a JSON string into a SerializedGameState
 * @throws Error if parsing fails or validation fails
 */
export function parseSerializedState(jsonString: string): SerializedGameState {
  let parsed: unknown;

  try {
    parsed = JSON.parse(jsonString);
  } catch (e) {
    throw new Error(`Failed to parse JSON: ${e instanceof Error ? e.message : 'Unknown error'}`);
  }

  if (!validateSerializedState(parsed)) {
    throw new Error('Invalid serialized state structure');
  }

  return parsed;
}

/** Current serialization version */
export const SERIALIZATION_VERSION = '1.1.0';
